import React, {useCallback, useLayoutEffect, useMemo, useRef, useState} from "react";
import {Checkbox, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel} from "@mui/material";
import {Row} from "reactstrap";
import Button from "../../../base/button/button";
import {Tab, Tabs} from "../../../base/Tabs";
import ImportCsvFile, {ImportCsvFileSteps} from "../../../app-specific/import-csv-file";
import ToastService from "../../../../../core/services/toast";
import {createUUId} from "../../../../../core/services/utils/utils";
import {AutoFixHigh} from "@mui/icons-material";
import useAccessLevels from "../../../../hooks/use-access-levels";
import {QuickOrderSteps} from "../index";
import {OrderItems} from "./order-items";
import {ImportWithAISection} from "./importi-with-ai-section";
import {setReduxDialogState} from "../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../core/models/static/routes/redux-store-routes";
import {useDispatch} from "react-redux";
import {ReactComponent as CheckboxIcon} from "../../../../../assets/images/check-box/checkbox.svg";
import {ReactComponent as ActiveCheckboxIcon} from "../../../../../assets/images/check-box/checkbox-active.svg";
import {createQuickOrderTarget, useSuggestiveSelling} from "../../../../features/suggestive-selling";

const QuickOrderTabs = {
    orderItems: 'order-items',
    importCsv: 'import-csv',
    importWithAI: 'import-with-AI',
}

function isEmptyQuickOrderRow(item) {
    return !item?.partNumber?.toUpperCase()?.trim();
}

const initialCsvFileState = {
    step: ImportCsvFileSteps.dropzone,
    parsedColumns: [],
    parsedRows: [],
}

const QuickOrderImportPartsSection = ({
                                          close,
                                          changeDialogStatus,
                                          items,
                                          setItems,
                                          customer,
                                          warehouses,
                                          validatePartNo,
                                          getAvailabilityForPartNo,
                                          getPriceForCustomerAndPartNo,
                                          setCustomer,
                                          setOrderExtraInformation,
                                          setDialogWidth,
                                      }) => {
    const formId = useMemo(() => createUUId(), []);
    const [tab, setTab] = useState(QuickOrderTabs.orderItems);
    const [itemEntities, setItemEntities] = useState([]);
    const [actions, setActions] = useState([]);
    const operatorAccessLevels = useAccessLevels();
    const dispatch = useDispatch();
    const {offerAfterAdd, isQuickOrderSuggestiveSuppressed} = useSuggestiveSelling();
    const offeredSuggestivePartsRef = useRef(new Set());

    const [importCsvFileState, setImportCsvFileState] = useState(initialCsvFileState);
    const [replaceImports, setReplaceImports] = useState(false);

    const defaultWarehouse = useMemo(() => warehouses.find(e => e.code === '00') ?? warehouses[0], [warehouses]);

    const quickOrderSalesOptions = useMemo(() => ({
        allowDuplicatePartNumbersInOrders: operatorAccessLevels.packages.sales.allowDuplicatePartNumbersInOrders,
        manageWarehousesInQuote: operatorAccessLevels.packages.sales.manageWarehousesInQuote,
    }), [
        operatorAccessLevels.packages.sales.allowDuplicatePartNumbersInOrders,
        operatorAccessLevels.packages.sales.manageWarehousesInQuote,
    ]);

    /**
     * Callback function that will be passed to the suggestive selling mechanism in order to add items to the quick order when a suggestive selling option is selected. It receives an object with the part number, quantity and warehouse code of the item to be added.
     * @type {(function({partNumber: *, qty: *, warehouseCode: *}): void)|*}
     */
    const onDraftItemAdded = useCallback(({partNumber, qty, warehouseCode}) => {
        const normalizedPartNumber = partNumber?.toUpperCase()?.trim();
        if (normalizedPartNumber) {
            offeredSuggestivePartsRef.current.add(normalizedPartNumber);
        }
        setItemEntities((prevState) => {
            const existing = prevState.find((e) => e.partNumber?.toUpperCase()?.trim() === normalizedPartNumber);
            if (existing && !quickOrderSalesOptions.allowDuplicatePartNumbersInOrders) {
                return prevState.map((e) => (
                    e.id === existing.id
                        ? {...e, qty: (Number(e.qty) || 0) + qty, isValid: true, partNumber: normalizedPartNumber}
                        : e
                ));
            }
            const emptyRowIndex = prevState.findIndex(isEmptyQuickOrderRow);
            if (emptyRowIndex >= 0) {
                return prevState.map((e, index) => (
                    index === emptyRowIndex
                        ? {
                            ...e,
                            partNumber: normalizedPartNumber,
                            qty,
                            warehouseCode: warehouseCode ?? defaultWarehouse?.code,
                            isValid: true,
                        }
                        : e
                ));
            }
            return [
                ...prevState,
                {
                    id: createUUId(),
                    partNumber: normalizedPartNumber,
                    qty,
                    warehouseCode: warehouseCode ?? defaultWarehouse?.code,
                    isValid: true,
                },
            ];
        });
    }, [defaultWarehouse?.code, quickOrderSalesOptions.allowDuplicatePartNumbersInOrders]);

    const buildQuickOrderTarget = useCallback(() => createQuickOrderTarget({
        customerId: customer?.id,
        defaultWarehouseCode: defaultWarehouse?.code,
        getDraftItems: () => itemEntities,
        onDraftItemAdded,
        salesOptions: quickOrderSalesOptions,
    }), [customer?.id, defaultWarehouse?.code, itemEntities, onDraftItemAdded, quickOrderSalesOptions]);

    const hasSuggestiveBeenOfferedForPart = useCallback((partNumber) => {
        const key = partNumber?.toUpperCase()?.trim();
        return !!key && offeredSuggestivePartsRef.current.has(key);
    }, []);

    const handlePartFirstValidated = useCallback(async ({partId, partNumber, sourceQuantity}) => {
        const key = partNumber?.toUpperCase()?.trim();
        if (!key || offeredSuggestivePartsRef.current.has(key) || isQuickOrderSuggestiveSuppressed()) {
            return;
        }
        offeredSuggestivePartsRef.current.add(key);
        await offerAfterAdd({
            sourcePartId: partId,
            sourcePartNumber: key,
            sourceQuantity,
            target: buildQuickOrderTarget(),
        });
    }, [buildQuickOrderTarget, isQuickOrderSuggestiveSuppressed, offerAfterAdd]);

    /**
     * Creates a default item object
     * @type {function(): *}
     */
    const createDefaultItem = useCallback(() => ({
        id: createUUId(),
        warehouseCode: defaultWarehouse?.code,
        partNumber: undefined,
        qty: 0,
        isValid: undefined,
        price: undefined,
    }), [defaultWarehouse?.code])

    const fields = useMemo(() => {
        const _fields = [
            {
                name: 'partNumber',
                title: 'Part Number',
                generator: (index) => `PART-${(index + 1).toString().padStart(5, '0')}`,
                format: (e) => e?.toString()?.toUpperCase()?.trim()
            },
            {
                name: 'quantity',
                title: 'Quantity',
                generator: (index) => `${index + 1}`,
                optional: true,
                format: (e) => {
                    let quantity = parseFloat(e?.toString()?.trim() || "1");
                    if (isNaN(quantity)) quantity = 1;

                    if (!operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders) {
                        quantity = Math.trunc(quantity);
                    }
                    if (!operatorAccessLevels.packages.sales.issueCredit) {
                        quantity = Math.max(0, quantity);
                    }
                    return quantity;
                }
            }
        ];

        if (operatorAccessLevels.packages.sales.manageWarehousesInQuote) {
            _fields.push({
                name: 'warehouseCode',
                title: 'Warehouse Code',
                optional: true,
                generator: (index) => defaultWarehouse?.code ?? `WHS-${index + 1}`,
                format: (e) => {
                    const str = e?.toString()?.trim();
                    return str ? str : defaultWarehouse?.code;
                }
            });
        }

        return _fields;
    }, [defaultWarehouse?.code, operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders, operatorAccessLevels.packages.sales.issueCredit, operatorAccessLevels.packages.sales.manageWarehousesInQuote]);


    /**
     * As soon as the component mounts:
     * - fills up the item entities state value either form the items props of the component or creates placeholder
     * entries.
     */
    useLayoutEffect(() => {
        const entities = [
            ...(items?.map((e) => ({
                id: createUUId(),
                warehouseCode: e.warehouseCode ?? defaultWarehouse?.code,
                partNumber: e.partNumber,
                qty: e.qty,
                isValid: e?.isValid ?? undefined,
                price: e?.price ?? undefined,
            })) ?? []),
            ...(Array.from({length: Math.max(5 - (items?.length ?? 0), 0)}, () => createDefaultItem())),
        ];
        setItemEntities(entities)
    }, [createDefaultItem, defaultWarehouse?.code, items])


    /**
     * Handles changes in the selected tab and sets the tab index to the received [newValue] in the props
     * @type {(function(*, *): void)}
     */
    const onTabChange = useCallback(async (event, newValue) => {
        if (tab === QuickOrderTabs.importWithAI && newValue !== QuickOrderTabs.importWithAI) {
            // We need to ask the user confirmation before leaving the AI import section
            const confirmed = await new Promise((resolve) => {
                dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.confirmation, {
                    open: true,
                    title: 'Leave Import With AI?',
                    description: 'Are you sure you want to leave the Import With AI section? Any unsaved data will be lost.',
                    confirmButtonText: 'Leave',
                    cancelButtonText: 'Stay',
                    maxWidth: 'sm',
                    fullWidth: false,
                    onClose: (confirmed) => {
                        resolve(confirmed);
                        return Promise.resolve(true);
                    }
                }));
            })
            if (!confirmed)
                return;
        }
        setTab(newValue);

    }, [dispatch, tab])

    /**
     * Callback function passed to the last element on the items card to add another row when "Tab" key is pressed
     * @type {(function(*=, *=): void)}
     */
    const addNewRow = useCallback((event, isFromButton) => {
        if (!(isFromButton || event.key === "Tab"))
            return;
        setItemEntities(prevState => [...prevState, createDefaultItem()])
    }, [createDefaultItem])

    /**
     * Removes the item in the list at the given index
     * @type {(function(*): void)}
     */
    const removeRow = useCallback((id) => {
        setItemEntities(prevState => prevState.filter((e) => e.id !== id));
    }, [])

    /**
     * Updates the item in the state based on the received object
     * @type {(function(*, *): void)}
     */
    const updateRow = useCallback((id, item) => {
        setItemEntities(prevState => prevState.map(e => e.id === id ? {...e, ...item} : e))
    }, [])

    /**
     * Callback function passed to the dropzone component of the CSV import section, receives the parsed rows and columns
     * @type {(function({rows: *, columns: *}): void)|*}
     */
    const onDropzoneParsed = useCallback(({rows, columns}) => {
        setImportCsvFileState({
            step: ImportCsvFileSteps.columnMapper,
            parsedColumns: columns,
            parsedRows: rows,
        });
    }, []);

    /**
     * Callback function passed to the column mapper component of the CSV import section, receives the parsed rows
     * @type {(function(*): Promise<void>)|*}
     */
    const onColumnMapperParsed = useCallback(async (parsedRows) => {
        // Remove empty part numbers or undefined parts
        const validRows = parsedRows.filter(e => !!e.partNumber);

        if (!validRows.length) {
            ToastService.error({
                title: "Error",
                message: "No valid part numbers found in the selected mapping.",
            })
            setImportCsvFileState(initialCsvFileState);
            return;
        }

        let newItems = validRows.map((e) => {
            const defaultItem = createDefaultItem();
            return {
                ...defaultItem,
                partNumber: e.partNumber,
                qty: e.quantity ?? 1,
                warehouseCode: e.warehouseCode ? warehouses.find(w => w.code === e.warehouseCode)?.code : defaultItem.warehouseCode,
            }
        });

        let partNumbers = [...new Set(newItems.map(t => t.partNumber))];
        for (const partNo of partNumbers) {
            const isValid = await validatePartNo(partNo);
            if (!isValid) {
                newItems = newItems.filter(e => e.partNumber !== partNo);
            }
        }

        // redo it since some might be invalid.
        partNumbers = [...new Set(newItems.map(t => t.partNumber))];

        for (const partNo of partNumbers) {
            getAvailabilityForPartNo(partNo);
        }

        // if the allow duplicate parts setting is disabled, group duplicate items together in mapping
        if (!operatorAccessLevels.packages.sales.allowDuplicatePartNumbersInOrders) {
            const aggregated = {};
            for (const item of newItems) {
                const key = `${item.partNumber}-${item.warehouseCode}`;
                if (!aggregated[key]) {
                    aggregated[key] = item;
                } else {
                    aggregated[key].qty += item.qty;
                }
            }
            newItems = Object.values(aggregated);
        }

        setItemEntities(prevState => {
            const filteredPrevState = replaceImports ? [] : prevState.filter(e => !!e.partNumber);
            if (operatorAccessLevels.packages.sales.allowDuplicatePartNumbersInOrders) {
                return [...filteredPrevState, ...newItems];
            }

            const nextState = [...filteredPrevState];
            for (const newItem of newItems) {
                const existingItem = nextState.find(
                    e => e.partNumber === newItem.partNumber && e.warehouseCode === newItem.warehouseCode
                );
                if (existingItem) {
                    existingItem.qty += newItem.qty;
                } else {
                    nextState.push(newItem);
                }
            }
            return nextState;
        });

        ToastService.success({
            title: "Success",
            message: "Items imported successfully.",
        });

        setImportCsvFileState(initialCsvFileState);
        setTab(QuickOrderTabs.orderItems);
    }, [operatorAccessLevels.packages.sales.allowDuplicatePartNumbersInOrders, createDefaultItem, warehouses, validatePartNo, getAvailabilityForPartNo, replaceImports]);


    /**
     * Checks if all the items are validated or are unTouched
     * @type {(function(*): boolean)}
     */
    const commitItemSelection = useCallback((itemEntities) => {
        if (itemEntities.length < 1) {
            ToastService.error({
                title: "No Products Found",
                message: "Please add your desired products manually or using the import options",
                hideTicketSubmission: true,
            });
            return false;
        }
        if (itemEntities.every(e => e.qty === 0)) {
            ToastService.error({
                title: "Invalid Quantity",
                message: "At least one part must have a none zero quantity.",
                hideTicketSubmission: true,
            })
            return false;
        }
        const items = itemEntities.filter(e => !!e.partNumber);
        if (items.filter(e => !e.isValid).length) {
            ToastService.error({
                title: "Invalid Part Number Found",
                message: "One or more Part Numbers are invalid",
                hideTicketSubmission: true,
            })
            return false;
        }
        const anyAvailabilityIsComingSoon = items.some(e => e?.availabilities?.find(avail => avail.warehouseCode === e.warehouseCode)?.isComingSoon);
        if (anyAvailabilityIsComingSoon && operatorAccessLevels.packages.sales.manageWarehousesInQuote) {
            ToastService.error({
                title: "Coming Soon Item(s) Found",
                message: "One or more items are coming soon in their respective warehouses. Please remove them or create them in the warehouses to proceed.",
            });
            return false;
        }
        setItems(items);
        return true;
    }, [operatorAccessLevels.packages.sales.manageWarehousesInQuote, setItems])

    /**
     * Callback function passed to the AI import section
     * @type {(function(*,*=): void)}
     */
    const onImportWithAISuccess = useCallback((data) => {
        if (commitItemSelection(data.items)) {
            setOrderExtraInformation(data.orderExtraInformation);
            setCustomer(data.customer);
            changeDialogStatus(QuickOrderSteps.SummarySection);
        }
    }, [changeDialogStatus, commitItemSelection, setCustomer, setOrderExtraInformation])


    /**
     * Callback function to submit the import section and move to the customer selection step
     * @type {(function(): void)}
     */
    const submitImportSection = useCallback(() => {
        if (commitItemSelection(itemEntities)) {
            changeDialogStatus(QuickOrderSteps.CustomerSelection);
        }
    }, [changeDialogStatus, commitItemSelection, itemEntities])


    const tabs = React.useMemo(() => {
        return Object.values(QuickOrderTabs).map(e => ({
            value: e,
            name: (function () {
                switch (e) {
                    case QuickOrderTabs.orderItems:
                        return 'Order Items';
                    case QuickOrderTabs.importCsv:
                        return 'Import CSV';
                    case QuickOrderTabs.importWithAI:
                        return (
                            <div className={'d-flex align-items-center'}>
                                <AutoFixHigh className={'icon small'}/>
                                Import With AI
                            </div>
                        );
                    default:
                        return "";
                }
            })(),
        }));
    }, [])

    /**
     * Updates the actions based on the selected tab
     */
    useLayoutEffect(() => {
        switch (tab) {
            case QuickOrderTabs.orderItems:
                setActions([
                    <Button
                        key={'close-button'}
                        variant={'dialog'}
                        color={'primary'}
                        onClick={close}
                        borderRadius={'0 8px 0 8px'}
                    >
                        Close
                    </Button>,
                    <Button
                        key={'next-button'}
                        variant={'dialog'}
                        color={'primary'}
                        disabled={itemEntities?.filter(e => !!e.partNumber && e.isValid)?.length === 0}
                        onClick={submitImportSection}
                        borderRadius={'8px 0 8px 0'}
                    >
                        Next
                    </Button>
                ]);
                break;
            case QuickOrderTabs.importCsv:
                if (importCsvFileState.step === ImportCsvFileSteps.columnMapper) {
                    setActions([
                        <Button
                            key={'back-button'}
                            variant={'dialog'}
                            size={'medium'}
                            type={'button'}
                            color={'primary'}
                            borderRadius={'0 8px 0 8px'}
                            onClick={() => setImportCsvFileState(initialCsvFileState)}
                        >
                            Back
                        </Button>,
                        <Button
                            key={'proceed-button'}
                            variant={'dialog'}
                            size={'medium'}
                            color={'primary'}
                            type={'submit'}
                            borderRadius={'8px 0 8px 0'}
                            form={formId}
                        >
                            Proceed
                        </Button>
                    ]);
                } else {
                    setActions([
                        <Button
                            key={'close-button'}
                            variant={'dialog'}
                            color={'primary'}
                            borderRadius={'0 8px 0 8px'}
                            onClick={() => close()}
                        >
                            Close
                        </Button>,
                        <Button
                            key={'next-button'}
                            variant={'dialog'}
                            color={'primary'}
                            borderRadius={'8px 0 8px 0'}
                            disabled={itemEntities?.filter(e => !!e.partNumber && e.isValid)?.length === 0}
                            onClick={submitImportSection}
                        >
                            Next
                        </Button>
                    ]);
                }
                break;
            default:
                setActions([]);
                break;
        }
    }, [close, submitImportSection, tab, importCsvFileState.step, itemEntities, formId]);

    return (
        <>
            <DialogTitle id={'scroll-dialog-title'}>
                <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                    Quick Order
                </p>
                <Divider className='divider'/>
                <div className='w-100 flex-grow-1'>
                    <Tabs value={tab ?? false} onChange={onTabChange}>
                        {
                            tabs.map(tab => (
                                <Tab
                                    key={tab.value}
                                    label={tab.name}
                                    value={tab.value}
                                    {...(tab.value === QuickOrderTabs.orderItems && {
                                        minWidth: '33.33%'
                                    })}
                                    {...(tab.value === QuickOrderTabs.importCsv && {
                                        minWidth: '33.33%'
                                    })}
                                    {...(tab.value === QuickOrderTabs.importWithAI && {
                                        minWidth: '33.33%'
                                    })}
                                />
                            ))
                        }
                    </Tabs>
                </div>
            </DialogTitle>
            <DialogContent>
                {
                    tab === QuickOrderTabs.orderItems &&
                    <div className={'py-3'}>
                        <OrderItems
                            itemEntities={itemEntities}
                            addNewRow={addNewRow}
                            removeRow={removeRow}
                            updateRow={updateRow}
                            warehouses={warehouses}
                            validatePartNo={validatePartNo}
                            getAvailabilityForPartNo={getAvailabilityForPartNo}
                            getPriceForCustomerAndPartNo={getPriceForCustomerAndPartNo}
                            onPartFirstValidated={handlePartFirstValidated}
                            hasSuggestiveBeenOfferedForPart={hasSuggestiveBeenOfferedForPart}
                        />
                    </div>
                }
                {
                    tab === QuickOrderTabs.importCsv &&
                    <div className={'py-4 px-3'}>
                        <p className={'text-md text-color-2 mb-4 w-100 font-weight-bold d-block'}>
                            {importCsvFileState.step === ImportCsvFileSteps.dropzone && "Import CSV File"}
                            {importCsvFileState.step === ImportCsvFileSteps.columnMapper && "Column Mapping"}
                        </p>
                        <ImportCsvFile
                            step={importCsvFileState.step}
                            formId={formId}
                            fields={fields}
                            parsedColumns={importCsvFileState.parsedColumns}
                            parsedRows={importCsvFileState.parsedRows}
                            onDropzoneParsed={onDropzoneParsed}
                            onColumnMapperParsed={onColumnMapperParsed}
                            sampleFileTitle={"quick-order-import-sample.csv"}
                            dropzoneNotes={
                                <div className="import-notes">
                                    <p className={'placeholder-text text-center'}>
                                        Upload your csv file here by clicking or dragging and dropping.<br/>
                                        The system will only update the items that the csv file contains.
                                    </p>
                                    <FormControlLabel
                                        label="Replace existing items with the imported items"
                                        labelPlacement={'end'}
                                        sx={{marginBlockStart: 3, display: 'flex', justifyContent: 'center'}}
                                        control={
                                            <Checkbox
                                                size={'small'}
                                                sx={{padding: 0, paddingInlineEnd: 0.5}}
                                                disableRipple
                                                checked={replaceImports}
                                                onChange={() => setReplaceImports(!replaceImports)}
                                                icon={<CheckboxIcon/>}
                                                checkedIcon={<ActiveCheckboxIcon/>}
                                            />
                                        }
                                    />
                                </div>
                            }
                        />
                    </div>
                }
                {
                    tab === QuickOrderTabs.importWithAI &&
                    <div className={'p-3'}>
                        <ImportWithAISection
                            setDialogWidth={setDialogWidth}
                            defaultWarehouse={defaultWarehouse}
                            warehouses={warehouses}
                            getAvailabilityForPartNo={getAvailabilityForPartNo}
                            validatePartNo={validatePartNo}
                            getPriceForCustomerAndPartNo={getPriceForCustomerAndPartNo}
                            onSuccess={onImportWithAISuccess}
                            createDefaultItem={createDefaultItem}
                            setActions={setActions}
                        />
                    </div>
                }
            </DialogContent>
            {
                actions.length > 0 &&
                <DialogActions className={'p-0'}>
                    <Row className={'w-100 no-gutters'}>
                        <Divider className='divider'/>
                        <Row className='no-gutters justify-content-between align-items-center w-100'>
                            {actions}
                        </Row>
                    </Row>
                </DialogActions>
            }
        </>
    );
}

export default QuickOrderImportPartsSection;
